// Guru brand type (gurumood.com), self-hosted: Figtree for text, Bricolage Grotesque for titles. Served
// from this origin, so the strict CSP (font-src / style-src 'self') holds and no viewer page calls Google.
import '@fontsource-variable/figtree';
import '@fontsource-variable/bricolage-grotesque';
import './styles.css';
import { type LaunchParams, readLaunchParams, risApiRoot, withGateway } from './config';
import { DicomWebClient } from './dicomweb';
import { initImageLoading, type LoaderContext } from './loader';
import { loadStudy } from './study';
import { applyDefaultBindings, initTools } from './tools';
import { ViewerApp } from './app';
import { mark } from './perf';
import type { ViewerShell } from './shell';
import * as annotations from './annotations';
import * as overlay from './overlay';
import * as sync from './sync';
import * as header from './ui/header';
import * as studyBrowser from './ui/studybrowser';
import * as toolbar from './ui/toolbar';
import * as compact from './ui/compact';
import * as commands from './commands';
import * as cine from './ui/cine';
import * as contextMenu from './ui/contextmenu';
import * as keyboard from './ui/keyboard';
import * as mpr from './mpr';
import * as ris from './ris';
import * as keyImages from './ris/keyimages';
import * as assistant from './ris/assistant';
import { applyLaunchProtocol } from './ris/protocols';
import * as overlayPlanes from './display/overlayplanes';
import * as diagnostic from './ui/diagnostic';
import * as reorient from './display/reorient';
import { setRequestIdleTimeout } from './net';
import * as hubSession from './ris/hub-session';
import * as share from './ris/share';
import * as shareOpen from './ris/share-open';
import { messageDialog } from './ui/dialogs';
import * as patient from './patient';
import { type ViewerLayout, viewerLayout } from './patient/layout';
import * as referrer from './referrer';
import { referrerModuleList } from './referrer/policy';

type FeatureModule = { install(shell: ViewerShell): void };

/** Workspace modules by name, installed in this order once the study metadata is in. */
const WORKSPACE_MODULES: ReadonlyArray<readonly [string, FeatureModule]> = [
  ['diagnostic', diagnostic], ['reorient', reorient], ['header', header], ['studyBrowser', studyBrowser],
  ['commands', commands], ['cine', cine], ['compact', compact], ['toolbar', toolbar], ['contextMenu', contextMenu],
  ['keyboard', keyboard], ['overlay', overlay], ['overlayPlanes', overlayPlanes], ['sync', sync],
  ['annotations', annotations], ['keyImages', keyImages], ['ris', ris], ['share', share], ['assistant', assistant], ['mpr', mpr],
];
const FEATURE_MODULES: FeatureModule[] = WORKSPACE_MODULES.map(([, module]) => module);
/** The patient layout (src/patient/layout.ts): nothing of the workspace is wired. */
const PATIENT_MODULES: FeatureModule[] = [diagnostic, patient];
/** The referrer layout: the workspace without export, share, the assistant or ris writes (src/referrer/policy.ts). */
const REFERRER_MODULES: FeatureModule[] = referrerModuleList(WORKSPACE_MODULES, referrer);

function modulesFor(layout: Exclude<ViewerLayout, 'blocked'>): FeatureModule[] {
  if (layout === 'patient') return PATIENT_MODULES;
  return layout === 'referrer' ? REFERRER_MODULES : FEATURE_MODULES;
}

const UNKNOWN_GATEWAY = 'El enlace del visor no es válido para esta instalación.';
const SESSION_ENDED = {
  title: 'Sesión finalizada',
  text: 'Su sesión terminó o ya no tiene acceso a este estudio. Inicie sesión de nuevo para seguir viendo las imágenes.',
} as const;
const EXAM_REFUSED: Record<number, string> = {
  403: 'No tiene permiso para ver las imágenes de este examen.',
  404: 'El examen no existe o aún no tiene imágenes.',
  503: 'El PACS no está disponible en este momento. Intente de nuevo en unos minutos.',
};
const EXAM_FAILED = 'No fue posible abrir el examen.';
/** A ticket link whose ris session could not be read: the viewer does not guess which layout (patient or
 * workspace) the ticket stands for; it shows nothing and lets the user try again. */
const SESSION_BLOCKED = {
  title: 'No fue posible conectar con el servidor del visor',
  text: 'Las imágenes no se pueden mostrar en este momento. Revise su conexión e intente de nuevo.',
} as const;

/**
 * A "Compartir" link: the recipient has no account; ris answers the share's ticket, after its access code
 * when the link has one. Returns the shared studies, or null when the viewer stops here (message shown).
 */
async function openFromShare(app: ViewerApp, risRoot: string, shareId: string): Promise<string[] | null> {
  try {
    const studies = await shareOpen.openShare(app, risRoot, shareId);
    if (studies === null) app.showMessage(shareOpen.SHARE_OPEN_TEXT.codeTitle);
    return studies;
  } catch (error) {
    console.error('[viewer] share link could not be opened', error);
    app.showMessage(error instanceof shareOpen.ShareUnavailable ? shareOpen.SHARE_OPEN_TEXT.unavailable : shareOpen.SHARE_OPEN_TEXT.failed);
    return null;
  }
}

/**
 * An `?exam=` link on the hub's site: the ticket comes from the user's own hub session (renewed in
 * the background). Not logged in: off to the hub sign-in, which brings the user back here. Returns the
 * exam's studies, or null when the viewer stops here (a message is already on screen).
 */
async function openFromHub(app: ViewerApp, hub: hubSession.HubSessionConfig, risRoot: string, examId: string): Promise<string[] | null> {
  const ended = (): void => {
    messageDialog(app, SESSION_ENDED.title, SESSION_ENDED.text, 'session-ended-dialog', 'INICIAR SESIÓN');
    app.showMessage(SESSION_ENDED.title);
    document.querySelector('.session-ended-dialog button')?.addEventListener('click', () => window.location.assign(hubSession.loginUrl(hub)));
  };
  try {
    return (await hubSession.openExam(hub, risRoot, examId, ended)).studyUIDs;
  } catch (error) {
    if (error instanceof hubSession.NotLoggedIn) {
      window.location.assign(hubSession.loginUrl(hub));
      return null;
    }
    console.error('[viewer] exam could not be opened', error);
    app.showMessage(error instanceof hubSession.ExamRefused ? EXAM_REFUSED[error.status] ?? EXAM_FAILED : EXAM_FAILED);
    return null;
  }
}

async function boot(): Promise<void> {
  mark('boot');
  const requested = readLaunchParams(window.VIEWER_CONFIG);
  // A gateway the link names but this deployment does not know: the shell opens on the default
  // roots only to say so, and nothing is requested anywhere.
  const resolved = withGateway(window.VIEWER_CONFIG, requested);
  const { config, launch } = resolved ?? { config: window.VIEWER_CONFIG, launch: requested };
  setRequestIdleTimeout(config.requestIdleTimeoutSeconds);
  // A ticket link carries its ticket; an exam link reads the one its hub session keeps renewed.
  const ticket = launch.token ?? (() => hubSession.currentTicket() ?? shareOpen.currentTicket());
  const client = new DicomWebClient(
    config.dicomwebRoot,
    ticket,
    launch.mode === 'parity' ? { 'Cache-Control': 'no-store', Pragma: 'no-cache' } : {},
  );
  const ctx: LoaderContext = { mode: launch.mode, wadoUriRoot: config.wadoUriRoot, client };
  document.documentElement.dataset.loadMode = launch.mode;

  initImageLoading(ctx);
  initTools(config.magnifyFactor);
  applyDefaultBindings();

  const app = new ViewerApp(document.getElementById('app') as HTMLElement, config, ctx);
  compact.prepare(app); // the chrome mode before anything loads: no desktop flash on a phone
  (window as unknown as { __viewerShell?: unknown }).__viewerShell = app;
  if (resolved === null) {
    app.showMessage(UNKNOWN_GATEWAY);
    return;
  }
  const apiRoot = risApiRoot(config);
  let studyUIDs = launch.studyUIDs;
  if (launch.token === null && launch.examId !== null && config.hubSession && apiRoot !== null) {
    const opened = await openFromHub(app, config.hubSession, apiRoot, launch.examId);
    if (opened === null) return;
    studyUIDs = opened;
  } else if (launch.token === null && launch.shareId !== null && apiRoot !== null) {
    const shared = await openFromShare(app, apiRoot, launch.shareId);
    if (shared === null) return;
    studyUIDs = shared;
  }
  if (studyUIDs.length === 0) {
    app.showMessage('No se indicó ningún estudio.');
    return;
  }
  await openStudies(app, { client, apiRoot, ticket, launch, studyUIDs });
}

interface OpenRequest {
  client: DicomWebClient;
  apiRoot: string | null;
  ticket: string | null | (() => string | null);
  launch: LaunchParams;
  studyUIDs: string[];
}

/**
 * The ris session (who, which layout) and the study, then the layout's modules. Retried as a whole
 * from the "blocked" dialog, so a ticket link never falls back to a guessed layout.
 */
async function openStudies(app: ViewerApp, request: OpenRequest): Promise<void> {
  const { client, apiRoot, ticket, launch, studyUIDs } = request;
  // ris workspace (session, permissions, preferences) in parallel with the study metadata; none in the bench.
  const session = apiRoot === null ? Promise.resolve(null) : ris.prepare(apiRoot, ticket);
  const loading = loadStudy(client, studyUIDs[0], launch.mode);
  // Handled below once the session is known; a refused ticket also fails the study load.
  loading.catch(() => undefined);
  const status = await session;
  if (status === 'denied') {
    app.showMessage(ris.AUTH_DENIED.title);
    ris.showAuthDenied(app);
    return;
  }
  const layout = viewerLayout({
    status: status ?? 'none',
    patient: ris.isPatientSession(),
    referrer: ris.isReferrerSession(),
    ticketLaunch: launch.token !== null,
  });
  if (layout === 'blocked') {
    showSessionBlocked(app, () => void openStudies(app, request));
    return;
  }
  if (status === 'unavailable') app.toast(ris.SESSION_UNAVAILABLE, 'warning');
  try {
    const study = await loading;
    mark('study-metadata');
    if (study.series.length === 0) {
      app.showMessage('El estudio no tiene imágenes disponibles.');
      return;
    }
    app.begin(study);
    if (layout === 'referrer') referrer.prepare();
    for (const module of modulesFor(layout)) module.install(app);
    await app.open();
    if (layout === 'patient') return;
    // Every other study on the ticket (priors chosen in the RIS) joins the browser, in ticket order.
    await addTicketStudies(app, client, studyUIDs.slice(1), launch.mode);
    if (launch.protocolId && status === 'ok') await applyLaunchProtocol(app, launch.protocolId);
  } catch (error) {
    console.error('[viewer] study load failed', error);
    app.showMessage('No fue posible cargar el estudio.');
  }
}

function showSessionBlocked(app: ViewerApp, retry: () => void): void {
  app.showMessage(SESSION_BLOCKED.title);
  messageDialog(app, SESSION_BLOCKED.title, SESSION_BLOCKED.text, 'session-blocked-dialog', 'REINTENTAR');
  document.querySelector('.session-blocked-dialog button')?.addEventListener('click', retry, { once: true });
}

async function addTicketStudies(app: ViewerApp, client: DicomWebClient, uids: string[], mode: LoaderContext['mode']): Promise<void> {
  const studies = await Promise.all(uids.map((uid) => loadStudy(client, uid, mode).catch((error: unknown) => {
    console.warn('[viewer] study on the ticket could not be loaded', uid, error);
    return null;
  })));
  for (const study of studies) if (study && study.series.length > 0) app.addStudy(study);
}

void boot();
